import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useParams, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Layout, MobileLayout, Spinner } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import {
  getRecordingsPath,
  RECORDING_START_SCHEMA_PARAM,
  RECORDING_START_SERIES_PARAM,
} from '@/entities/transcriptor';

import { Header, MobileHeader } from '@/widgets/Header';
import {
  useRecordingSetup,
  useStartTemplate,
} from '@/widgets/TranscriptorRecordingModal';
import { TemplateStartWorkspace } from '@/widgets/TranscriptorWorkspace';

import styles from './PatientRecordingStart.module.scss';

// A new recording of the template card the address names, so a reload keeps
// it; "Templates" opens the template choice again.
const V2RecordingStart: FC = () => {
  const dispatch = useAppDispatch();
  const { patientID = '' } = useParams<{ patientID: string }>();
  const [searchParams] = useSearchParams();
  const { isMobile } = useMedia();

  const template = useStartTemplate({
    seriesId: searchParams.get(RECORDING_START_SERIES_PARAM) ?? undefined,
    schemaId: searchParams.get(RECORDING_START_SCHEMA_PARAM) ?? undefined,
  });
  const { catalogLoaded, language, microphone } = useRecordingSetup(
    template?.schemaId,
  );

  const openTemplates = () =>
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TranscriptorRecording,
        data: { patientID },
      }),
    );

  const content = template ? (
    <>
      <h1 className={cn('h4', styles.title)}>{template.name}</h1>
      <TemplateStartWorkspace
        // Another card chosen from "Templates" starts over.
        key={template.id}
        className={styles.workspace}
        patientId={patientID}
        template={template}
        language={language}
        microphone={microphone}
        onBack={openTemplates}
      />
    </>
  ) : catalogLoaded ? (
    <p className={cn('p3', styles.message)}>
      <FormattedMessage
        id="workspace.start.templateMissing"
        defaultMessage="This template is not available in the clinic any more."
      />
    </p>
  ) : (
    <div className={styles.loading}>
      <Spinner size={48} />
    </div>
  );

  const page = (
    <div className={styles.page} data-section="recording-start">
      {content}
    </div>
  );

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />
        <MobileLayout.Main>{page}</MobileLayout.Main>
      </MobileLayout>
    );
  }

  return (
    <Layout>
      <Layout.Header>
        <Header />
      </Layout.Header>
      <Layout.Content>
        <Layout.Main>{page}</Layout.Main>
      </Layout.Content>
    </Layout>
  );
};

export const PatientRecordingStart: FC = () => {
  const { patientID = '' } = useParams<{ patientID: string }>();
  const loaded = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationSettingsLoaded,
  );
  const enabled = useAppSelector(
    organizationModel.selectors.selectTranscriptorV2Enabled,
  );
  if (!loaded) {
    return <Spinner size={48} />;
  }
  return enabled ? (
    <V2RecordingStart />
  ) : (
    <Navigate replace to={getRecordingsPath(patientID)} />
  );
};
